import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Table from "../../../../Components/v2/Table";
import DateTimeCell from "../../../../Components/v2/Table/DateTimeCell";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import {
  addLocalNotification,
  clearAccountingTransaction,
  clearAcctTransactionError,
  clearDocumentExcelAccountError,
  requestDeleteAcctTransaction,
  requestGetParentAccountingTransaction,
  requestListAccount,
  requestListAccountingTransaction,
  requestListAccountTransaction,
  requestUpdateAcctTransaction,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useNotificationDispatch,
  usePeriodLock,
} from "../../../../Context";
import PeriodLockBanner from "../../../../Components/v2/Accounting/PeriodLockBanner";
import PeriodLockCell from "../../../../Components/v2/Accounting/PeriodLockCell";
import Pagination from "../../../../Components/v2/Pagination";
import {
  cancelAPIRequest,
  convertToTitleCase,
  currencyFormat,
  getErrorMessages,
  getHasPaymentsMessage,
  getPaymentCount,
} from "../../../../Utils";
import TableActions from "../../../../Components/v2/Table/TableActions";
import { isPayrollJournal } from "../../../../Utils/payroll";
import { isDepositTransaction } from "../../../../Utils/DepositUtil";
import { isStockAdjustmentJournal } from "../../../../Utils/AccountingUtil";
import DepositDetailModal from "../../../../Components/v2/Accounting/DepositDetailModal";

import { ModalAlert } from "../../../../Components/ModalAlert";
import Modal from "../../../../Components/v2/Modal";
import Button from "../../../../Components/v2/Button";
import TransactionModal from "../../../../Components/v2/Accounting/TransactionModal";
import { PlusIcon, PencilIcon } from "@heroicons/react/outline";
import { TRANSACTION_TYPES } from "../../../../Constants";
import InvoiceModal from "../../../../Components/v2/Accounting/InvoiceModal";
import InvoiceDetailModal from "../../../../Components/v2/Accounting/InvoiceDetailModal";
import InvoicePaymentModal from "../../../../Components/v2/Accounting/InvoicePaymentModal";
import TransactionDetailModal from "../../../../Components/v2/Accounting/TransactionDetailModal";
import ExpandableDescription from "../../../../Components/v2/Accounting/ExpandableDescription"; // Added for description expansion
import TransactionUploadModal from "../../../../Components/v2/Accounting/TransactionUploadModal";
import FormInput from "../../../../Components/v2/Form/FormInput";
import FormSelect from "../../../../Components/v2/Form/FormSelect";
import FormDateRange from "../../../../Components/v2/Form/FormDateRange";
import FormError from "../../../../Components/v2/Form/FormError";
import {
  toWibPickerDate,
  wibCurrentMonth,
  wibEndOfDay,
  wibStartOfDay,
} from "../../../../Utils/PeriodUtils";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const getTransactionTypeInfo = (type) => {
  const matched = TRANSACTION_TYPES.find((item) => item.value === type);
  const fallback = TRANSACTION_TYPES.find((item) => item.value === "OTHER");
  return {
    label: convertToTitleCase(type) || convertToTitleCase(fallback.value),
    ...(matched?.style || fallback.style),
  };
};

const AccountingTransactions = () => {
  const { t, i18n } = useTranslation();

  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [isTransactionDetailModalOpen, setIsTransactionDetailModalOpen] =
    useState(false);
  const [isTransactionUploadModalOpen, setIsTransactionUploadModalOpen] =
    useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isInvoiceDetailModalOpen, setIsInvoiceDetailModalOpen] =
    useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isInvoicePaymentModalOpen, setIsInvoicePaymentModalOpen] =
    useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [transactionType, setTransactionType] = useState(null);
  const [selectedAccountFilter, setSelectedAccountFilter] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const accountId = searchParams.get("accountId");
  const startDateParam = searchParams.get("startDate");
  const endDateParam = searchParams.get("endDate");

  const [dateRange, setDateRange] = useState(() => {
    if (startDateParam && endDateParam) {
      // Report links carry WIB day boundaries as UTC instants; read them back
      // as the WIB days they stand for, not the laptop's.
      return [toWibPickerDate(startDateParam), toWibPickerDate(endDateParam)];
    }
    return wibCurrentMonth();
  });
  const [startDate, endDate] = dateRange;
  const [isEditAmountModalOpen, setIsEditAmountModalOpen] = useState(false);
  const [editingValue, setEditingValue] = useState("");
  const [editingError, setEditingError] = useState(null);

  const accountingDispatch = useAccountingDispatch();
  const notificationDispatch = useNotificationDispatch();

  const {
    request,
    transactions,
    accounts,
    accountTransactions,
    accountTransactionsLoading,
    accountTransactionsError,
    error: accountingError,
    loading: accountingLoading,
    nextTokens,
    accountTransactionNextTokens,
    deletedID,
    submitError,
    submitLoading,
    transactionID,
    allAccountLoading,
    accountError,
    parentTransaction,
    parentTransactionLoading,
  } = useAccountingState();
  const { user } = useAuthState();
  // A deposit row opens its deposit, where it can be changed, not a
  // transaction form.
  const [depositDetailID, setDepositDetailID] = useState(null);
  const { isDateClosed } = usePeriodLock(user?.mainBusiness?.id);

  const isAccountFiltered = Boolean(selectedAccountFilter);
  const loading = isAccountFiltered
    ? accountTransactionsLoading
    : accountingLoading;
  const error = isAccountFiltered ? accountTransactionsError : accountingError;
  const activeNextTokens = isAccountFiltered
    ? accountTransactionNextTokens
    : nextTokens;

  // Clear errors on unmount
  useEffect(() => {
    return () => {
      if (request) {
        cancelAPIRequest(request);
      }
      clearAccountingTransaction(accountingDispatch);
      clearAcctTransactionError(accountingDispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAccounts = () => {
    if (user.mainBusiness?.id) {
      requestListAccount(accountingDispatch, {
        page: 1,
        businessID: user.mainBusiness.id,
        loadAll: true,
      });
    }
  };

  useEffect(() => {
    if (accounts.length === 0) loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountingDispatch, user.mainBusiness?.id]);

  const loadTransactions = () => {
    if (!user.mainBusiness?.id) return;
    // Don't fetch if we are waiting for account filter from URL
    if (accountId && !selectedAccountFilter) return;
    // Don't fetch if only one date is selected (incomplete range)
    if ((startDate && !endDate) || (!startDate && endDate)) return;

    const createdAt =
      startDate && endDate
        ? {
            between: [
              wibStartOfDay(startDate),
              wibEndOfDay(endDate),
            ],
          }
        : undefined;

    setPage(1);
    if (selectedAccountFilter) {
      requestListAccountTransaction(accountingDispatch, {
        page: 1,
        limit: perPage,
        accountID: selectedAccountFilter.value,
        createdAt,
      });
    } else {
      requestListAccountingTransaction(accountingDispatch, {
        page: 1,
        limit: perPage,
        businessID: user.mainBusiness.id,
        createdAt,
      });
    }
  };

  // Re-fetch when account filter, date range, or perPage changes
  useEffect(() => {
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedAccountFilter,
    perPage,
    accountId,
    startDate,
    endDate,
    user.mainBusiness?.id,
  ]);

  useEffect(() => {
    if (accountId && accounts.length > 0) {
      const account = accounts.find((acc) => acc.id === accountId);
      if (account) {
        setSelectedAccountFilter({
          value: account.id,
          label: `${account.code} - ${account.name}`,
        });
      } else {
        // Invalid accountId in URL — clear it to avoid stuck state
        setSearchParams({});
      }
    } else if (!accountId) {
      setSelectedAccountFilter(null);
    }
  }, [accountId, accounts, setSearchParams]);

  //  Handle delete success
  useEffect(() => {
    if (deletedID) {
      // Close modal
      setShowConfirmModal(false);
      clearAccountingTransaction(accountingDispatch);
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `delete-transaction-${deletedID}`,
        type: "success",
        title: "common.success",
        body: "transactions.notifications.deleteSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, deletedID, accountingDispatch]);

  // Handle inline edit success
  useEffect(() => {
    if (transactionID && isEditAmountModalOpen) {
      addLocalNotification(notificationDispatch, {
        id: `edit-transaction-${transactionID}`,
        type: "success",
        title: "common.success",
        body: "transactions.notifications.updateSuccess",
        showFull: true,
      });
      setSelectedTransaction(null);
      setEditingValue("");
      setEditingError(null);
      setIsEditAmountModalOpen(false);
      clearAccountingTransaction(accountingDispatch);
    }
  }, [
    transactionID,
    isEditAmountModalOpen,
    notificationDispatch,
    accountingDispatch,
  ]);

  useEffect(() => {
    if (submitError) {
      setShowConfirmModal(false);
      // A failed amount edit stays open with what was typed and says why, so
      // it can be corrected and sent again.
      if (isEditAmountModalOpen) {
        setEditingError(getErrorMessages(submitError, t).join(" "));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  useEffect(() => {
    if (
      submitError &&
      !showConfirmModal &&
      !isEditAmountModalOpen &&
      !isTransactionModalOpen &&
      !isInvoiceModalOpen &&
      !isInvoiceDetailModalOpen &&
      !isInvoicePaymentModalOpen
    ) {
      setShowErrorModal(true);
    }
  }, [
    submitError,
    showConfirmModal,
    isEditAmountModalOpen,
    isTransactionModalOpen,
    isInvoiceModalOpen,
    isInvoiceDetailModalOpen,
    isInvoicePaymentModalOpen,
  ]);

  const onPageChange = (selectedPage) => {
    setPage(selectedPage);
    const createdAt =
      startDate && endDate
        ? {
            between: [
              wibStartOfDay(startDate),
              wibEndOfDay(endDate),
            ],
          }
        : undefined;

    // An account filter pages through its own token list, not the full one.
    if (activeNextTokens[selectedPage] || selectedPage === 1) {
      if (selectedAccountFilter) {
        requestListAccountTransaction(accountingDispatch, {
          page: selectedPage,
          limit: perPage,
          accountID: selectedAccountFilter.value,
          nextToken: activeNextTokens[selectedPage],
          createdAt,
        });
      } else {
        requestListAccountingTransaction(accountingDispatch, {
          page: selectedPage,
          limit: perPage,
          businessID: user.mainBusiness.id,
          nextToken: nextTokens[selectedPage],
          createdAt,
        });
      }
    }
  };

  const onPerPageChange = (itemsPerPage) => {
    setPerPage(itemsPerPage);
  };

  // The three write entry points all re-check the lock rather than trusting the
  // cell that called them: a row can be locked by another tab between render and
  // click, and `handleEdit` is also reachable from the detail modal.
  const handleDelete = (transaction) => {
    if (isDateClosed(transaction?.date || transaction?.createdAt)) return;
    if (isPayrollJournal(transaction)) return;
    if (isStockAdjustmentJournal(transaction)) return;
    // Written by /deposit; /transaction refuses to change it.
    if (transaction?.isDeposit || isDepositTransaction(transaction)) return;
    setSelectedTransaction(transaction);
    setShowConfirmModal(true);
  };

  const handleClose = (shouldRefresh) => {
    cancelAPIRequest(request);
    setIsTransactionModalOpen(false);
    setIsTransactionDetailModalOpen(false);
    setSelectedTransaction(null);
    setIsInvoiceModalOpen(false);
    setIsTransactionUploadModalOpen(false);
    clearDocumentExcelAccountError(accountingDispatch);
    clearAccountingTransaction(accountingDispatch);
    if (shouldRefresh && user.mainBusiness?.id) {
      setPage(1);
      if (selectedAccountFilter) {
        requestListAccountTransaction(accountingDispatch, {
          page: 1,
          limit: perPage,
          accountID: selectedAccountFilter.value,
        });
      } else {
        requestListAccountingTransaction(accountingDispatch, {
          page: 1,
          limit: perPage,
          businessID: user.mainBusiness.id,
        });
      }
    }
  };

  const handleEdit = (transaction) => {
    if (isDateClosed(transaction?.createdAt)) return;
    if (isPayrollJournal(transaction)) return;
    if (isStockAdjustmentJournal(transaction)) return;
    // Written by /deposit; /transaction refuses to change it.
    if (transaction?.isDeposit || isDepositTransaction(transaction)) return;
    setSelectedTransaction(transaction);
    if (transaction?.invoiceNumber) {
      setIsInvoiceModalOpen(true);
    } else if (transaction?.parentTransactionID) {
      setSelectedPayment(transaction);
      requestGetParentAccountingTransaction(accountingDispatch, {
        id: transaction.parentTransactionID,
      });
      setIsInvoicePaymentModalOpen(true);
    } else {
      setIsTransactionModalOpen(true);
    }
  };

  const handleStartEdit = (transaction) => {
    if (isDateClosed(transaction?.createdAt)) return;
    if (isPayrollJournal(transaction)) return;
    if (isStockAdjustmentJournal(transaction)) return;
    // Written by /deposit; /transaction refuses to change it.
    if (transaction?.isDeposit || isDepositTransaction(transaction)) return;
    setSelectedTransaction(transaction);
    setEditingValue(transaction.total.toString());
    setEditingError(null);
    setIsEditAmountModalOpen(true);
  };

  const handleCancelEdit = () => {
    cancelAPIRequest(request);
    setSelectedTransaction(null);
    setEditingValue("");
    setEditingError(null);
    setIsEditAmountModalOpen(false);
    // The failure was shown in this modal; don't raise it again on close.
    clearAcctTransactionError(accountingDispatch);
  };

  const handleSaveEdit = () => {
    if (isDateClosed(selectedTransaction?.createdAt)) return;
    if (!editingValue || editingValue.trim() === "") {
      setEditingError("transactions.errors.amountRequired");
      return;
    }

    const newTotal = Number(editingValue);
    if (isNaN(newTotal) || newTotal <= 0) {
      setEditingError("accounting.transactions.modal.error.invalidAmount");
      return;
    }

    setEditingError(null);

    const input = {
      transaction_id: selectedTransaction.id,
      business_id: user.mainBusiness.id,
      accounts:
        selectedTransaction.accounts?.items?.map((account) => ({
          id: account.accountID,
          debit: account.debit > 0 ? newTotal : 0,
          credit: account.credit > 0 ? newTotal : 0,
        })) || [],
      type: selectedTransaction.type,
      paid: selectedTransaction.paid,
      created_at: new Date(selectedTransaction.createdAt),
      description: selectedTransaction.description,
      total: newTotal,
      payment_due_at: new Date(selectedTransaction.paymentDueAt),
      partner_id: selectedTransaction.partnerID || null,
    };

    requestUpdateAcctTransaction(accountingDispatch, { input });
  };

  // The server refuses to delete an invoice or bill that still has payments, so
  // the dialog says so up front and its button stays disabled.
  const selectedPaymentCount = getPaymentCount(selectedTransaction);

  const onDelete = () => {
    if (selectedPaymentCount > 0) return;
    requestDeleteAcctTransaction(accountingDispatch, {
      id: selectedTransaction?.id,
    });
  };

  const getDeleteDescription = () => {
    if (!selectedTransaction) return null;
    const date = dayjs(selectedTransaction.date)
      .locale(i18n.language)
      .format("D MMM YYYY");
    const amount = currencyFormat(selectedTransaction.amount?.total ?? 0);
    const summary = selectedTransaction.description
      ? t("accounting.transactions.modal.description.deleteItem", {
          description: selectedTransaction.description,
          amount,
          date,
        })
      : t("accounting.transactions.modal.description.deleteItemNoDesc", {
          amount,
          date,
        });
    if (selectedPaymentCount === 0) return summary;
    // Spans, not divs: ModalAlert renders this inside a <p>.
    return (
      <>
        {summary}
        <span className="mt-2 block font-medium text-red-600">
          {t("accounting.transactions.modal.description.hasPayments", {
            count: selectedPaymentCount,
          })}
        </span>
      </>
    );
  };

  const dropDownItems = useMemo(
    () => [
      {
        name: t("accounting.transactions.action.invoice"),
        onClick: () => setIsInvoiceModalOpen(true),
      },
      {
        name: t("accounting.transactions.action.income"),
        onClick: () => {
          setTransactionType("INCOME");
          setIsTransactionModalOpen(true);
        },
      },
      {
        name: t("accounting.transactions.action.expense"),
        onClick: () => {
          setTransactionType("EXPENSE");
          setIsTransactionModalOpen(true);
        },
      },
      {
        name: t("accounting.transactions.action.upload"),
        onClick: () => {
          setIsTransactionUploadModalOpen(true);
          clearDocumentExcelAccountError(accountingDispatch);
        },
      },
    ],
    [t, accountingDispatch],
  );

  // Normalize data from both sources into a unified shape
  const activeTransactions = useMemo(() => {
    if (isAccountFiltered) {
      // accountTransactions items are join records: { transaction: {...}, account: {...}, debit, credit, ... }
      return accountTransactions.map((at) => ({
        ...at.transaction,
        accounts: { items: [at] },
      }));
    }
    return transactions;
  }, [isAccountFiltered, accountTransactions, transactions]);

  const transformedTransactions = useMemo(() => {
    return activeTransactions.map((transaction) => {
      const typeInfo = getTransactionTypeInfo(transaction.type);

      return {
        id: transaction.id,
        payrollRunID: transaction.payrollRunID,
        stockAdjustmentID: transaction.stockAdjustmentID,
        depositID: transaction.depositID,
        isDeposit: isDepositTransaction(transaction),
        // Kept for the delete dialog's payment check.
        payments: transaction.payments,
        date: transaction.createdAt,
        description:
          transaction.description ||
          (transaction.partner
            ? `${t("accounting.dashboard.transaction.transactionWith", {
                partnerName: transaction.partner.name,
              })}`
            : transaction.invoiceNumber
              ? `Invoice #${transaction.invoiceNumber}`
              : null),
        type: {
          label: typeInfo.label,
          colorClass: typeInfo.colorClass,
        },
        amount: {
          total: transaction.total,
          value: currencyFormat(transaction.total),
          colorClass: typeInfo.textColorClass,
          prefix: typeInfo.prefix,
        },
      };
    });
  }, [activeTransactions, t]);

  const accountFilterOptions = useMemo(() => {
    return accounts.map((account) => ({
      value: account.id,
      label: `${account.code} - ${account.name}`,
    }));
  }, [accounts]);

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        {/* Header */}
        <AccountingPageHeader
          title="accounting.transactions.title"
          dropDownAction={{
            label: t("accounting.transactions.header.title.add"),
            onClick: () => setIsTransactionModalOpen(true),
            icon: <PlusIcon className="h-5 w-5" />,
            items: dropDownItems,
          }}
        />

        {/* Transaction List */}
        <div className="px-4 py-5 sm:px-6 lg:px-8">
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormSelect
              searchable
              isLoading={allAccountLoading}
              disabled={loading}
              placeholder="accounting.transactions.filter.allAccounts"
              options={accountFilterOptions}
              value={selectedAccountFilter}
              onChange={(option) => {
                if (option?.value) {
                  setSearchParams({ accountId: option.value });
                } else {
                  setSearchParams({});
                }
              }}
              onClear={() => setSearchParams({})}
            />
            <FormDateRange
              startDate={startDate}
              endDate={endDate}
              onChange={(update) => {
                setDateRange(update);
              }}
              placeholderText={t("accounting.transactions.filter.dateRange")}
              onClear={() => setDateRange([null, null])}
              disabled={loading}
            />
          </div>
          <PeriodLockBanner
            businessID={user?.mainBusiness?.id}
            className="mb-4"
          />
          {/* The account filter and an ?accountId= link both wait on this
              list; without it the page reads as having no transactions. */}
          <FormError
            className="mb-4"
            error={accountError}
            title="accounting.transactions.filter.accountsLoadError"
            onRetry={loadAccounts}
          />
          <Table
            isLoading={loading}
            error={error}
            onRetry={loadTransactions}
            data={transformedTransactions}
            columns={[
              {
                header: "accounting.dashboard.transaction.field.date",
                accessor: (transaction) => (
                  <DateTimeCell value={transaction.date} />
                ),
                sortable: true,
                sortKey: "date",
              },
              {
                header: "accounting.dashboard.transaction.field.description",
                accessor: (transaction) => {
                  const hasDescription = !!transaction.description;
                  return (
                    <div className="max-w-[200px] lg:max-w-xs whitespace-normal break-words">
                      {hasDescription ? (
                        <ExpandableDescription text={transaction.description} />
                      ) : (
                        <span className="text-gray-400 italic">
                          {t("accounting.dashboard.transaction.noDescription")}
                        </span>
                      )}
                    </div>
                  );
                },
              },
              {
                header: "accounting.dashboard.transaction.field.type",
                accessor: (transaction) => (
                  <span
                    className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${transaction.type.colorClass}`}
                  >
                    {t(transaction.type.label)}
                  </span>
                ),
              },
              {
                header: "accounting.dashboard.transaction.field.amount",
                accessor: (transaction, index) => {
                  const originalTransaction = activeTransactions[index];
                  // This cell is a hidden write affordance — clicking the amount
                  // opens an edit modal. A locked row keeps the number readable
                  // but drops the click target, hover state, cursor, role and
                  // pencil together; leaving any one of them would promise an
                  // edit that cannot happen.
                  const editable =
                    !originalTransaction?.invoiceNumber &&
                    !isPayrollJournal(originalTransaction) &&
                    !isStockAdjustmentJournal(originalTransaction) &&
                    !isDepositTransaction(originalTransaction) &&
                    !isDateClosed(transaction.date);

                  return (
                    <div
                      className={`flex items-center justify-end px-2 py-1 rounded-md transition-colors duration-200 -mr-2 ${
                        editable ? "cursor-pointer group hover:bg-gray-100" : ""
                      }`}
                      onClick={() => {
                        if (editable) {
                          handleStartEdit(originalTransaction);
                        }
                      }}
                      role={editable ? "button" : undefined}
                      title={
                        editable
                          ? t("accounting.dashboard.transaction.clickToEdit")
                          : ""
                      }
                    >
                      <span
                        className={`${transaction.amount.colorClass} font-medium`}
                      >
                        {transaction.amount.prefix}
                        {transaction.amount.value}
                      </span>
                      <div className="w-3.5 h-3.5 ml-2 flex-shrink-0">
                        {editable && (
                          <PencilIcon
                            className="w-full h-full text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    </div>
                  );
                },
                align: "right",
              },
              {
                header: "common.actions.label",
                accessor: (transaction, index) => {
                  const viewTransaction = () => {
                    if (
                      isDepositTransaction(activeTransactions[index]) &&
                      activeTransactions[index]?.depositID
                    ) {
                      setDepositDetailID(activeTransactions[index].depositID);
                    } else if (activeTransactions[index]?.invoiceNumber) {
                      setSelectedInvoice(activeTransactions[index]);
                      setIsInvoiceDetailModalOpen(true);
                    } else {
                      const tx = activeTransactions[index];
                      setSelectedTransaction(tx);
                      if (tx?.parentTransactionID) {
                        requestGetParentAccountingTransaction(
                          accountingDispatch,
                          { id: tx.parentTransactionID },
                        );
                      }
                      setIsTransactionDetailModalOpen(true);
                    }
                  };

                  // Whole cell swapped, never a filtered action list:
                  // TableActions renders an inline button for one action and a
                  // dots menu for two or more, so pruning Edit/Delete would
                  // silently change the control's shape as well as its meaning.
                  if (isDateClosed(transaction.date)) {
                    return (
                      <PeriodLockCell
                        className="justify-end"
                        onView={viewTransaction}
                      />
                    );
                  }

                  if (transaction.isDeposit) {
                    return (
                      <PeriodLockCell
                        className="justify-end"
                        label="deposits.lockedRow"
                        onView={viewTransaction}
                      />
                    );
                  }

                  // A stock-adjustment journal changes only with the stock (Atur Stok
                  // Produk); the server refuses it here.
                  if (isStockAdjustmentJournal(transaction)) {
                    return (
                      <PeriodLockCell
                        className="justify-end"
                        label="accounting.stockAdjustmentJournal.lockedRow"
                        onView={viewTransaction}
                      />
                    );
                  }

                  // A payroll journal changes only on the Payroll page (void,
                  // cancel payment); the server refuses it here.
                  if (isPayrollJournal(transaction)) {
                    return (
                      <PeriodLockCell
                        className="justify-end"
                        label="payroll.journal.lockedRow"
                        onView={viewTransaction}
                      />
                    );
                  }

                  return (
                    <TableActions
                      position={
                        index === transformedTransactions.length - 1
                          ? "top"
                          : "bottom"
                      }
                      actions={[
                        {
                          label: t("common.view"),
                          onClick: viewTransaction,
                        },
                        {
                          label: t("common.edit"),
                          onClick: () => handleEdit(activeTransactions[index]),
                        },
                        {
                          label: t("common.delete"),
                          onClick: () => handleDelete(transaction),
                          variant: "danger",
                        },
                      ]}
                    />
                  );
                },
                align: "right",
              },
            ]}
          />
        </div>

        {/* Pagination */}
        <Pagination
          nextTokens={activeNextTokens}
          currentPage={page}
          isLoading={loading}
          perPage={perPage}
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
        />

        {isTransactionModalOpen && (
          <TransactionModal
            open={isTransactionModalOpen}
            onClose={handleClose}
            transaction={selectedTransaction}
            transactionType={transactionType}
          />
        )}

        {isTransactionDetailModalOpen && (
          <TransactionDetailModal
            open={isTransactionDetailModalOpen}
            onClose={() => {
              setIsTransactionDetailModalOpen(false);
              setSelectedTransaction(null);
              clearAccountingTransaction(accountingDispatch);
            }}
            onEdit={(transaction) => {
              setIsTransactionDetailModalOpen(false);
              handleEdit(transaction);
            }}
            onViewParentInvoice={(parent) => {
              setIsTransactionDetailModalOpen(false);
              setSelectedTransaction(null);
              if (parent?.invoiceNumber) {
                setSelectedInvoice(parent);
                setIsInvoiceDetailModalOpen(true);
              } else {
                setSelectedTransaction(parent);
                setIsTransactionDetailModalOpen(true);
              }
            }}
            transaction={selectedTransaction}
            parentTransaction={parentTransaction}
            parentTransactionLoading={parentTransactionLoading}
          />
        )}

        {/* Invoice Modal */}
        {isInvoiceModalOpen && (
          <InvoiceModal
            open={isInvoiceModalOpen}
            onClose={handleClose}
            invoice={selectedTransaction}
          />
        )}

        {/* Invoice Detail Modal */}
        {isInvoiceDetailModalOpen && (
          <InvoiceDetailModal
            open={isInvoiceDetailModalOpen}
            invoice={selectedInvoice}
            onClose={() => {
              setIsInvoiceDetailModalOpen(false);
              setSelectedInvoice(null);
            }}
            onEdit={(invoice) => {
              setIsInvoiceDetailModalOpen(false);
              setSelectedTransaction(invoice);
              setIsInvoiceModalOpen(true);
            }}
          />
        )}

        {depositDetailID && (
          <DepositDetailModal
            open={Boolean(depositDetailID)}
            depositID={depositDetailID}
            businessID={user?.mainBusiness?.id}
            onClose={() => setDepositDetailID(null)}
            // A refund, forfeit, delete or undo changes the rows on this page.
            onChanged={() => onPageChange(page)}
          />
        )}

        {/* Invoice Payment Edit Modal */}
        {isInvoicePaymentModalOpen && (
          <InvoicePaymentModal
            open={isInvoicePaymentModalOpen}
            payment={selectedPayment}
            parentInvoice={parentTransaction?.id ? parentTransaction : null}
            onClose={() => {
              setIsInvoicePaymentModalOpen(false);
              setSelectedPayment(null);
              clearAccountingTransaction(accountingDispatch);
            }}
          />
        )}

        {isTransactionUploadModalOpen && (
          <TransactionUploadModal
            open={isTransactionUploadModalOpen}
            onClose={handleClose}
          />
        )}

        {/* Delete Modal */}
        <ModalAlert
          open={showConfirmModal}
          setOpen={(open) => {
            setShowConfirmModal(open);
          }}
          title="accounting.transactions.modal.title.delete"
          description="accounting.transactions.modal.description.delete"
          descriptionNode={getDeleteDescription()}
          confirmButtonText="common.delete"
          cancelButtonText="Cancel"
          loadingButton={submitLoading}
          disabledButton={submitLoading || selectedPaymentCount > 0}
          onConfirm={onDelete}
          onCancel={() => {
            cancelAPIRequest(request);
            setShowConfirmModal(false);
            setSelectedTransaction(null);
          }}
        />

        {/* Error Modal */}
        {submitError && (
          <ErrorDialog
            open={showErrorModal}
            message={getHasPaymentsMessage(submitError)}
            error={submitError}
            onClose={() => {
              setShowErrorModal(false);
              clearAcctTransactionError(accountingDispatch);
            }}
          />
        )}

        {/* Edit Amount Modal */}
        <Modal
          open={isEditAmountModalOpen}
          onClose={handleCancelEdit}
          title="accounting.dashboard.transaction.clickToEdit"
          footer={
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={handleCancelEdit}
                variant="secondary"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                onClick={handleSaveEdit}
                disabled={submitLoading}
                variant="primary"
              >
                {t("common.save")}
              </Button>
            </div>
          }
        >
          <FormInput
            label={t("transactions.fields.amount")}
            value={editingValue}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !submitLoading) {
                handleSaveEdit();
              }
            }}
            // The plain number is kept, as in TransactionModal: fed back as
            // "3.000", the formatted text had its "." read as a decimal point.
            numberFormat={{
              thousandSeparator: ".",
              decimalSeparator: ",",
              allowedDecimalSeparators: [","],
              valueIsNumericString: true,
              onValueChange: ({ value }) => {
                setEditingValue(value);
                if (editingError) setEditingError(null);
              },
            }}
            prefix="Rp"
            error={editingError}
            disabled={submitLoading}
            onClear={() => setEditingValue("")}
            autoFocus
          />
        </Modal>
      </main>
    </AccountingWrapper>
  );
};

export default AccountingTransactions;
